import type React from 'react'
import { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import type { Church, Campus, OccurrenceWithEvent } from '@fellowship/shared'
import { MapPin, Phone, Mail, Globe } from 'lucide-react'
import { ChurchLocationMap } from '../components/ChurchLocationMap'
import { GRADIENT, EventOccurrenceCard } from './ChurchProfilePage'

interface ChurchProfileMobileProps {
  church: Church
  address: string
  occurrences: OccurrenceWithEvent[]
  occurrencesLoading: boolean
  campuses: Campus[]
  isAuthed: boolean
  following: boolean
  onToggleFollow: () => void
  loginNext: string
  onShare: () => void
}

const CARD_STYLE: React.CSSProperties = {
  background: '#fff',
  borderRadius: 20,
  padding: 16,
  boxShadow: '0 4px 18px rgba(31,26,46,.06)',
}

const EYEBROW_STYLE: React.CSSProperties = {
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: '.1em',
  color: '#8b8398',
}

// Parses labels like "9:15 AM" / "10:30am" into minutes-past-midnight so the
// quick-facts strip can surface the earliest service without re-sorting the
// service-times list itself (the list must render in the order given).
function parseTimeToMinutes(time: string | null | undefined): number {
  if (!time) return Infinity
  const m = time.trim().match(/^(\d{1,2}):(\d{2})\s*(AM|PM)?$/i)
  if (!m) return Infinity
  let h = parseInt(m[1], 10)
  const min = parseInt(m[2], 10)
  const ampm = m[3]?.toUpperCase()
  if (ampm === 'PM' && h !== 12) h += 12
  if (ampm === 'AM' && h === 12) h = 0
  return h * 60 + min
}

function scrollToId(id: string) {
  const el = document.getElementById(id)
  if (!el) return
  const top = el.getBoundingClientRect().top + window.scrollY - 64
  window.scrollTo({ top, behavior: 'smooth' })
}

export function ChurchProfileMobile({
  church,
  address,
  occurrences,
  occurrencesLoading,
  campuses,
  isAuthed,
  following,
  onToggleFollow,
  loginNext,
  onShare,
}: ChurchProfileMobileProps) {
  const [aboutExpanded, setAboutExpanded] = useState(false)
  const [aboutOverflows, setAboutOverflows] = useState(false)
  const [addressCopied, setAddressCopied] = useState(false)
  const aboutRef = useRef<HTMLParagraphElement>(null)

  useEffect(() => {
    const el = aboutRef.current
    if (el) setAboutOverflows(el.scrollHeight > el.clientHeight + 1)
  }, [church.description])

  const hasAddress = Boolean(address)
  const hasContact = Boolean(church.phone || church.email || church.websiteUrl)

  const earliestService =
    church.serviceTimes.length > 0
      ? [...church.serviceTimes].sort(
          (a, b) => parseTimeToMinutes(a.time) - parseTimeToMinutes(b.time),
        )[0]
      : null

  const contactChannels = [church.phone && 'Call', church.email && 'Email'].filter(
    Boolean,
  ) as string[]

  const quickFacts: { key: string; eyebrow: string; value: string; anchor: string }[] = []
  if (earliestService) {
    quickFacts.push({ key: 'sabbath', eyebrow: 'SABBATH', value: earliestService.time, anchor: 'services' })
  }
  if (church.city) {
    quickFacts.push({ key: 'where', eyebrow: 'WHERE', value: church.city, anchor: 'visit' })
  }
  if (contactChannels.length > 0) {
    quickFacts.push({ key: 'contact', eyebrow: 'CONTACT', value: contactChannels.join(' · '), anchor: 'contact' })
  }

  async function copyAddress() {
    try {
      await navigator.clipboard.writeText(address)
    } catch {
      const el = document.createElement('input')
      el.value = address
      document.body.appendChild(el)
      el.select()
      document.execCommand('copy')
      document.body.removeChild(el)
    }
    setAddressCopied(true)
    setTimeout(() => setAddressCopied(false), 2000)
  }

  return (
    <div style={{ paddingBottom: 84, overflowX: 'hidden' }}>
      {/* Unclaimed banner */}
      {!church.claimed && (
        <div
          style={{
            background: '#fffbeb',
            borderBottom: '1px solid #fcd34d',
            padding: '10px 16px',
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            gap: 8,
          }}
        >
          <span style={{ fontSize: 13, color: 'var(--ink)', flex: '1 1 200px' }}>
            ⚠️ This listing was created automatically. Is this your church?
          </span>
          <Link
            to={`/churches/${church.id}/claim`}
            style={{
              padding: '6px 14px',
              borderRadius: 8,
              background: '#d97706',
              color: '#fff',
              fontSize: 12.5,
              fontWeight: 600,
              textDecoration: 'none',
              flexShrink: 0,
            }}
          >
            Claim this page
          </Link>
        </div>
      )}

      {/* Cover photo */}
      <div style={{ position: 'relative', width: '100%', height: 172, overflow: 'hidden' }}>
        {church.coverImageUrl ? (
          <img
            src={church.coverImageUrl}
            alt=""
            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
          />
        ) : (
          <div
            style={{
              width: '100%',
              height: '100%',
              background:
                'repeating-linear-gradient(135deg, var(--primary-soft) 0 20px, var(--surface-2) 20px 42px)',
            }}
          />
        )}
        <div
          style={{
            position: 'absolute',
            inset: 0,
            background:
              'linear-gradient(to bottom, rgba(31,26,46,.28) 0%, rgba(31,26,46,0) 40%, rgba(31,26,46,.5) 100%)',
          }}
        />
        <div
          style={{
            position: 'absolute',
            left: 16,
            right: 16,
            top: 12,
            display: 'flex',
            flexWrap: 'wrap',
            gap: 6,
          }}
        >
          {church.denomination && (
            <span
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                height: 26,
                padding: '0 11px',
                borderRadius: 999,
                background: 'rgba(255,255,255,.94)',
                fontSize: 12,
                fontWeight: 600,
                letterSpacing: '-.01em',
                color: '#1f1a2e',
              }}
            >
              {church.denomination.replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())}
            </span>
          )}
          {church.city && (
            <span
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 4,
                height: 26,
                padding: '0 11px',
                borderRadius: 999,
                background: 'rgba(255,255,255,.94)',
                fontSize: 12,
                fontWeight: 600,
                color: '#1f1a2e',
              }}
            >
              <MapPin size={10} /> {church.city}, {church.state}
            </span>
          )}
        </div>
      </div>

      {/* Logo tile row */}
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-end',
          gap: 12,
          padding: '0 16px',
          marginTop: -26,
          position: 'relative',
          zIndex: 2,
        }}
      >
        <div
          style={{
            flexShrink: 0,
            width: 68,
            height: 68,
            borderRadius: 18,
            background: '#fff',
            border: '3px solid var(--bg)',
            boxShadow: '0 6px 18px rgba(31,26,46,.12)',
            overflow: 'hidden',
            display: 'grid',
            placeItems: 'center',
          }}
        >
          {church.logoUrl ? (
            <img
              src={church.logoUrl}
              alt={church.name}
              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
            />
          ) : (
            <div style={{ width: 32, height: 32, background: GRADIENT, borderRadius: 8, opacity: 0.4 }} />
          )}
        </div>
        <div style={{ minWidth: 0, paddingBottom: 4 }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              fontSize: 13,
              color: '#5a526d',
              fontWeight: 500,
            }}
          >
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
              <span
                style={{ width: 6, height: 6, borderRadius: 999, background: '#5B8B6B', display: 'inline-block' }}
              />
              {occurrences.length} upcoming
            </span>
            <span style={{ color: '#cfc6bb' }}>·</span>
            <span>
              {church.followerCount} follower{church.followerCount === 1 ? '' : 's'}
            </span>
          </div>
        </div>
      </div>

      {/* Name + tagline */}
      <div style={{ padding: '12px 16px 0' }}>
        <h1
          style={{
            margin: 0,
            fontSize: 30,
            lineHeight: 1.08,
            fontWeight: 700,
            letterSpacing: '-.033em',
          }}
        >
          {church.name}
        </h1>
        {church.tagline && (
          <p style={{ margin: '8px 0 0', fontSize: 15, lineHeight: 1.45, color: '#5a526d' }}>
            {church.tagline}
          </p>
        )}
      </div>

      {/* Quick-facts strip */}
      {quickFacts.length > 0 && (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: `repeat(${quickFacts.length}, 1fr)`,
            gap: 8,
            padding: '16px 16px 4px',
          }}
        >
          {quickFacts.map((f) => (
            <a
              key={f.key}
              href={`#${f.anchor}`}
              onClick={(e) => {
                e.preventDefault()
                scrollToId(f.anchor)
              }}
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
                padding: '11px 10px',
                borderRadius: 14,
                background: '#fff',
                boxShadow: '0 2px 10px rgba(31,26,46,.05)',
                color: 'inherit',
                textDecoration: 'none',
              }}
            >
              <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: '.09em', color: '#8b8398' }}>
                {f.eyebrow}
              </span>
              <span style={{ fontSize: 14, fontWeight: 600, letterSpacing: '-.02em' }}>{f.value}</span>
            </a>
          ))}
        </div>
      )}

      {/* Service times */}
      {church.serviceTimes.length > 0 && (
        <div id="services" style={{ padding: '14px 16px 0', scrollMarginTop: 64 }}>
          <div style={CARD_STYLE}>
            <div style={{ ...EYEBROW_STYLE, marginBottom: 12 }}>SERVICE TIMES</div>
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              {church.serviceTimes.map((st, i) => (
                <div
                  key={i}
                  style={{
                    display: 'flex',
                    alignItems: 'baseline',
                    justifyContent: 'space-between',
                    gap: 12,
                    padding: '9px 0',
                    borderBottom: i < church.serviceTimes.length - 1 ? '1px solid #f4eedf' : 'none',
                  }}
                >
                  <span style={{ fontSize: 15, fontWeight: 600, letterSpacing: '-.015em' }}>{st.name}</span>
                  <span style={{ fontSize: 14, color: '#5a526d', whiteSpace: 'nowrap' }}>
                    {[st.day, st.time].filter(Boolean).join(' ')}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Visit */}
      {hasAddress && (
        <div id="visit" style={{ padding: '12px 16px 0', scrollMarginTop: 64 }}>
          <div style={CARD_STYLE}>
            <div style={{ ...EYEBROW_STYLE, marginBottom: 10 }}>VISIT</div>
            <div style={{ display: 'flex', gap: 8, fontSize: 15, lineHeight: 1.4, fontWeight: 500 }}>
              <MapPin size={16} color="#8b8398" style={{ flexShrink: 0, marginTop: 2 }} />
              <span>
                {church.addressLine1}
                {church.addressLine1 && <br />}
                {[church.city, church.state, church.zip].filter(Boolean).join(', ')}
              </span>
            </div>
            <div style={{ marginTop: 12, borderRadius: 14, overflow: 'hidden' }}>
              <ChurchLocationMap lat={church.locationLat} lng={church.locationLng} label={church.name} height={148} />
            </div>
            <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
              <a
                href={`https://www.google.com/maps/search/?api=1&query=${church.locationLat},${church.locationLng}`}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  flex: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: 44,
                  borderRadius: 999,
                  background: 'var(--bg-alt)',
                  fontSize: 14,
                  fontWeight: 600,
                  color: '#1f1a2e',
                  textDecoration: 'none',
                }}
              >
                Directions →
              </a>
              <button
                type="button"
                onClick={copyAddress}
                style={{
                  flex: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: 44,
                  borderRadius: 999,
                  border: '1px solid #ece5d6',
                  background: 'transparent',
                  fontSize: 14,
                  fontWeight: 600,
                  color: '#1f1a2e',
                  fontFamily: 'inherit',
                  cursor: 'pointer',
                }}
              >
                {addressCopied ? 'Copied!' : 'Copy address'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Campuses */}
      {campuses.length > 1 && (
        <div style={{ padding: '12px 16px 0' }}>
          <div style={CARD_STYLE}>
            <div style={{ ...EYEBROW_STYLE, marginBottom: 10 }}>CAMPUSES</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {campuses.map((campus) => {
                const campusAddress = [campus.addressLine1, campus.city, campus.state]
                  .filter(Boolean)
                  .join(', ')
                return (
                  <div
                    key={campus.id}
                    style={{ padding: '10px 12px', borderRadius: 10, background: 'var(--bg-alt)' }}
                  >
                    <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 2 }}>{campus.name}</div>
                    {campusAddress && (
                      <div style={{ fontSize: 12.5, color: '#5a526d' }}>{campusAddress}</div>
                    )}
                  </div>
                )
              })}
            </div>
          </div>
        </div>
      )}

      {/* Contact */}
      {hasContact && (
        <div id="contact" style={{ padding: '12px 16px 0', scrollMarginTop: 64 }}>
          <div style={{ ...CARD_STYLE, padding: '8px 16px' }}>
            {church.phone && (
              <a
                href={`tel:${church.phone}`}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                  minHeight: 52,
                  borderBottom: church.email || church.websiteUrl ? '1px solid #f4eedf' : 'none',
                  color: 'inherit',
                  textDecoration: 'none',
                }}
              >
                <span
                  style={{
                    width: 34,
                    height: 34,
                    borderRadius: 999,
                    background: 'var(--bg-alt)',
                    display: 'grid',
                    placeItems: 'center',
                    flexShrink: 0,
                  }}
                >
                  <Phone size={14} color="#5a526d" />
                </span>
                <span style={{ fontSize: 15, fontWeight: 500 }}>{church.phone}</span>
              </a>
            )}
            {church.email && (
              <a
                href={`mailto:${church.email}`}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                  minHeight: 52,
                  borderBottom: church.websiteUrl ? '1px solid #f4eedf' : 'none',
                  color: 'inherit',
                  textDecoration: 'none',
                }}
              >
                <span
                  style={{
                    width: 34,
                    height: 34,
                    borderRadius: 999,
                    background: 'var(--bg-alt)',
                    display: 'grid',
                    placeItems: 'center',
                    flexShrink: 0,
                  }}
                >
                  <Mail size={14} color="#5a526d" />
                </span>
                <span style={{ fontSize: 15, fontWeight: 500, overflowWrap: 'anywhere' }}>{church.email}</span>
              </a>
            )}
            {church.websiteUrl && (
              <a
                href={church.websiteUrl}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                  minHeight: 52,
                  color: 'inherit',
                  textDecoration: 'none',
                }}
              >
                <span
                  style={{
                    width: 34,
                    height: 34,
                    borderRadius: 999,
                    background: 'var(--bg-alt)',
                    display: 'grid',
                    placeItems: 'center',
                    flexShrink: 0,
                  }}
                >
                  <Globe size={14} color="#5a526d" />
                </span>
                <span style={{ fontSize: 15, fontWeight: 500 }}>
                  {church.websiteUrl.replace(/^https?:\/\//, '')}
                </span>
              </a>
            )}
          </div>
        </div>
      )}

      {/* Upcoming events */}
      <div style={{ padding: '22px 16px 0' }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
          <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700, letterSpacing: '-.025em' }}>Upcoming events</h2>
          <span style={{ fontSize: 15, color: '#8b8398' }}>{occurrences.length}</span>
        </div>
        {occurrencesLoading && (
          <p style={{ marginTop: 10, fontSize: 13, color: 'var(--muted)' }}>Loading…</p>
        )}
        {!occurrencesLoading && occurrences.length === 0 && (
          <div
            style={{
              marginTop: 10,
              padding: '20px 16px',
              borderRadius: 18,
              background: 'var(--bg-alt)',
              border: '1px dashed #e0d6c4',
              textAlign: 'center',
            }}
          >
            <div style={{ fontSize: 15, fontWeight: 600 }}>No gatherings scheduled yet</div>
            <div style={{ marginTop: 4, fontSize: 14, color: '#5a526d', lineHeight: 1.45 }}>
              Follow this church to hear first when something is posted.
            </div>
          </div>
        )}
        {occurrences.length > 0 && (
          <div style={{ marginTop: 10, display: 'flex', flexDirection: 'column', gap: 10 }}>
            {occurrences.map((occ) => (
              <EventOccurrenceCard key={occ.id} occurrence={occ} />
            ))}
          </div>
        )}
      </div>

      {/* About us */}
      {church.description && (
        <div style={{ padding: '22px 16px 0' }}>
          <h2 style={{ margin: '0 0 8px', fontSize: 20, fontWeight: 700, letterSpacing: '-.025em' }}>About us</h2>
          <p
            ref={aboutRef}
            style={{
              margin: 0,
              fontSize: 15,
              lineHeight: 1.6,
              color: '#5a526d',
              display: '-webkit-box',
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              WebkitLineClamp: aboutExpanded ? 'unset' : 4,
            }}
          >
            {church.description}
          </p>
          {aboutOverflows && (
            <button
              type="button"
              onClick={() => setAboutExpanded((v) => !v)}
              style={{
                marginTop: 8,
                padding: 0,
                border: 0,
                background: 'transparent',
                fontFamily: 'inherit',
                fontSize: 14,
                fontWeight: 600,
                color: '#3B4CCA',
                cursor: 'pointer',
              }}
            >
              {aboutExpanded ? 'Show less' : 'Read more'}
            </button>
          )}
        </div>
      )}

      {/* Fixed bottom action bar */}
      <div
        style={{
          position: 'fixed',
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 40,
          display: 'flex',
          justifyContent: 'center',
          pointerEvents: 'none',
        }}
      >
        <div
          style={{
            width: 390,
            maxWidth: '100%',
            padding: '10px 16px calc(14px + env(safe-area-inset-bottom))',
            display: 'flex',
            gap: 10,
            background: 'rgba(250,246,240,.94)',
            backdropFilter: 'blur(14px)',
            borderTop: '1px solid #ece5d6',
            pointerEvents: 'auto',
          }}
        >
          {isAuthed ? (
            <button
              type="button"
              aria-label={following ? 'Unfollow church' : 'Follow church'}
              onClick={onToggleFollow}
              className="cp-m-press"
              style={{
                flex: 1,
                height: 48,
                border: 0,
                borderRadius: 999,
                fontFamily: 'inherit',
                fontSize: 16,
                fontWeight: 600,
                letterSpacing: '-.01em',
                cursor: 'pointer',
                color: '#fff',
                background: GRADIENT,
                boxShadow: '0 6px 18px rgba(59,76,202,.32)',
              }}
            >
              {following ? '✓ Following' : '+ Follow'}
            </button>
          ) : (
            <Link
              to={loginNext}
              aria-label="Sign in to follow this church"
              className="cp-m-press"
              style={{
                flex: 1,
                height: 48,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: 999,
                fontFamily: 'inherit',
                fontSize: 16,
                fontWeight: 600,
                letterSpacing: '-.01em',
                color: '#fff',
                background: GRADIENT,
                boxShadow: '0 6px 18px rgba(59,76,202,.32)',
                textDecoration: 'none',
              }}
            >
              + Follow
            </Link>
          )}
          <button
            type="button"
            onClick={onShare}
            className="cp-m-press"
            style={{
              flex: '0 0 auto',
              width: 84,
              height: 48,
              border: '1px solid #ece5d6',
              borderRadius: 999,
              background: '#fff',
              fontFamily: 'inherit',
              fontSize: 15,
              fontWeight: 600,
              color: '#1f1a2e',
              cursor: 'pointer',
            }}
          >
            Share
          </button>
        </div>
      </div>
      <style>{`
        .cp-m-press:active { transform: scale(.98); }
        @media (prefers-reduced-motion: no-preference) { .cp-m-press { transition: transform .1s; } }
      `}</style>
    </div>
  )
}
